import { useMutation } from '@apollo/client';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { CREATE_QUOTATION_MUTATION } from '../../../api/mutations/quotations/quotation';
import { QUOTATIONS_QUERY } from '../../../api/queries/quotations/quotation';
import { useAppDispatch } from '../../../helpers/reduxHooks';
import { classNames } from '../../../helpers/utils';
import LoadingIndicator from '../../../layout/LoadingIndicator';
import { setSuccessAlert } from '../../../redux/alertSlice';

interface Props {  
  handleClose: () => void,
}

export default function CreateQuotationModal(props: Props) {
  const { handleClose } = props;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { projectId, quotationId } = useParams();
  const { t } = useTranslation();

  const [
    createQuotation,
    {
      data: mutationData,
      loading: mutationLoading,
    }] = useMutation(CREATE_QUOTATION_MUTATION, { 
    refetchQueries: [
      { query: QUOTATIONS_QUERY,
        variables: {
          project: projectId,
        },
      },
    ],
  });
    
  const handleCreate = () => {
    if (quotationId) createQuotation({ 
      variables: { 
        quotation: quotationId,
      },
      onError: () => handleClose(),
    });
  };

  useEffect(() => {
    if (mutationData && mutationData.createQuotation && mutationData.createQuotation.response) {
      dispatch(setSuccessAlert(
        [`${mutationData.createQuotation.response.name} quotation was successfully created.`],
      ));
      handleClose();
      navigate(`/quotation/${projectId}/${mutationData.createQuotation.response.id}/`);  
    }
  }, [mutationData, navigate, dispatch, projectId, handleClose]);

  return (
    <div className='flex flex-col justify-between items-center w-full rounded drop-shadow-xl bg-white' onClick={e => e.stopPropagation()}>
      <div className='pl-8 bg-cblue-100 w-full h-14 border-b-2 border-cblue-500 font-bold flex items-center text-cblue-500'>{t('Create New Quotation')}</div>
      <div className='w-full pl-8 my-8'>{t('This Action will create the copy of current Quotation')}</div>
      <div className='flex justify-end gap-8 pr-8 pb-8 w-full'>
        <button
          onClick={() => !mutationLoading && handleClose()}
          type="button"
          className={classNames('w-1/5 flex justify-center items-center px-4 py-2 border border-cblue-500 rounded text-sm font-medium uppercase text-cblue-500', 
            mutationLoading ? 'cursor-wait bg-cgray-300 text-white border-none' : 'hover:bg-cblue-200 focus:bg-cblue-300 active:bg-cblue-300',
          )}
        >
          {t('Cancel')}
        </button>
        <button
          disabled={mutationLoading}
          onClick={handleCreate}
          type="submit"
          className={classNames('w-1/5 relative flex justify-center items-center px-4 py-2 rounded text-sm font-medium uppercase text-white bg-cblue-500 transition duration-150', 
            mutationLoading ? 'cursor-wait bg-cgray-300 text-white' : 'hover:bg-cblue-700 hover:opacity-90 focus:bg-cblue-700 focus:opacity-100 active:bg-cblue-700 active:opacity-100')}
        >
          {mutationLoading ? <LoadingIndicator color="white" className="flex justify-center items-center absolute w-full h-full top-0 right-0" /> : t('Create')}
        </button>
      </div>
    </div>
  );
}
